import React, { useState } from 'react';
import { Modal } from '@components/molecules/Modal';
import { Button } from '@components/atoms/Button';
import { Input } from '@components/atoms/Input';
import { AlertTriangle, XCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from '@hooks/useTranslation';

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  organizationName: string;
  isLoading?: boolean;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  organizationName,
  isLoading = false,
}) => {
  const t = useTranslation('billing');
  const [confirmationText, setConfirmationText] = useState('');
  const [isCanceled, setIsCanceled] = useState(false);

  // Trimmed: a trailing space — trivially easy to introduce by copying the name from the
  // label — left the button disabled with nothing on screen explaining why, so pressing it
  // did nothing and read as a broken button.
  const isConfirmationValid =
    confirmationText.trim().toLowerCase() ===
    organizationName.trim().toLowerCase();

  const showMismatchHint = confirmationText.length > 0 && !isConfirmationValid;

  const handleConfirm = async () => {
    if (!isConfirmationValid) return;
    await onConfirm();
    setConfirmationText('');
    // Report the outcome here rather than relying on a toast. Cancelling ends with a full
    // page load, which destroys the toast, and the app then auto-switches to another
    // organization — so the user landed on an ordinary dashboard with no evidence that
    // anything had happened. This screen is the one place the confirmation cannot be lost.
    setIsCanceled(true);
  };

  const handleClose = () => {
    setConfirmationText('');
    onClose();
  };

  if (isCanceled) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title=""
        size="small"
        showCloseButton={false}>
        <div className="space-y-4 pt-4 text-center">
          <div className="flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {t.subscription.canceledTitle}
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              <span className="font-semibold">{organizationName}</span>{' '}
              {t.subscription.canceledBodySuffix}
            </p>
          </div>
          <Button
            variant="primary"
            size="md"
            fullWidth
            onClick={() => {
              // Full load on purpose: cancelling changes organization-wide state that is
              // spread across Redux, React Query and storage, and a clean boot is the
              // only cheap way to be sure none of it is left stale.
              window.location.href = '/organization-suspended';
            }}>
            {t.subscription.continueButton}
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      size="small"
      showCloseButton={false}>
      <div className="space-y-4 pt-4">
        {/* Warning Icon */}
        <div className="flex justify-center">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
        </div>

        {/* Title */}
        <div className="text-center">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {t.subscription.cancelSubscriptionButton}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t.subscription.undoneNoPeriod}
          </p>
        </div>

        {/* Consequences */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
          <h3 className="text-xs font-semibold text-red-800 dark:text-red-300 mb-2">
            {t.subscription.consequencesHeading}
          </h3>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-red-700 dark:text-red-300">
                <strong>{t.subscription.consequenceGrandfatheringBold}</strong>{' '}
                {t.subscription.consequenceGrandfatheringBody}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-red-700 dark:text-red-300">
                <strong>{t.subscription.consequenceAccessBold}</strong>{' '}
                {t.subscription.consequenceAccessBody}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-red-700 dark:text-red-300">
                <strong>{t.subscription.consequenceEmployeesBold}</strong>{' '}
                {t.subscription.consequenceEmployeesBody}
              </span>
            </li>
          </ul>
        </div>

        {/* Confirmation Input */}
        <div>
          <label
            htmlFor="confirmationInput"
            className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {t.subscription.confirmTypePrefix}{' '}
            <span className="font-bold text-gray-900 dark:text-white">
              {organizationName}
            </span>{' '}
            {t.subscription.confirmTypeSuffix}
          </label>
          <Input
            id="confirmationInput"
            type="text"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder={t.subscription.confirmPlaceholder.replace('{org}', organizationName)}
            disabled={isLoading}
            error={showMismatchHint}
          />
          {showMismatchHint && (
            <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
              {t.subscription.mismatchPrefix}{' '}
              <span className="font-semibold">{organizationName}</span> {t.subscription.mismatchSuffix}
            </p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-2 pt-1">
          <Button
            variant="tertiary"
            size="md"
            fullWidth
            onClick={handleClose}
            disabled={isLoading}>
            {t.subscription.keepSubscriptionButton}
          </Button>
          <Button
            variant="primary"
            size="md"
            fullWidth
            onClick={handleConfirm}
            disabled={!isConfirmationValid}
            loading={isLoading}
            className="!bg-red-600 hover:!bg-red-700 active:!bg-red-800">
            {t.subscription.cancelSubscriptionButton}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CancelSubscriptionModal;
